<template>
  <div class="app-blog-container">
    <h1 class="app-page-title">{{ $t("common.Blog") }}</h1>
    <!-- <StoryblokComponent v-if="story" :blok="story.content" />    -->

    <!-- Add filter bar -->
    <div class="blog-filter-bar">
      <button
        class="filter-tag"
        :class="[`type-all`, { active: selectedFilter === 'all' }]"
        @click="selectedFilter = 'all'"
      >
        {{ $t("blog.All") }}
      </button>
      <button
        v-for="tag in ['news', 'how-to', 'project', 'feature']"
        :key="tag"
        class="filter-tag"
        :class="[`type-${tag}`, { active: selectedFilter === tag }]"
        @click="selectedFilter = tag"
      >
        {{ getBlogTypeString(tag) }}
      </button>
    </div>

    <div class="blog-posts-grid">
      <article v-for="post in filteredPosts" :key="post.slug" class="blog-post-card">
        <localized-link :to="`blogPost`" :params="{ slug: post.slug }" class="blog-post-link">
          <div class="blog-post-image">
            <img :src="getImagePath(post.image)" :alt="post.title" />
            <span class="blog-type-tag" :class="`type-${post.template}`">
              {{ getBlogTypeString(post.template) }}
            </span>
          </div>
          <div class="blog-post-content">
            <h2>{{ post.title }}</h2>
            <p class="blog-post-excerpt">{{ post.excerpt }}</p>
            <div class="blog-post-meta">
              <span class="blog-post-author">{{ post.author }}</span>
              <span class="blog-post-author-date-separator">|</span>
              <span class="blog-post-date">{{ formatDate(post.date) }}</span>
            </div>
          </div>
        </localized-link>
      </article>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from "vue";
import { getAllPosts } from "../utils/blogLoader";
import LocalizedLink from "../components/LocalizedLink.vue";
import { useRoute } from "vue-router";
import { getImagePath } from "@/lib/imageUtils";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const route = useRoute();
const blogPosts = ref([]);
const loading = ref(true);
const selectedFilter = ref("all");

const formatDate = (dateString) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
  } catch (error) {
    console.warn("Error formatting date:", error);
    return dateString;
  }
};

async function loadBlogPosts() {
  loading.value = true;
  const locale = route.params.locale;
  blogPosts.value = await getAllPosts(locale);
  loading.value = false;
}

onMounted(loadBlogPosts);

watch(
  () => route.params.locale,
  (newLocale) => {
    if (newLocale) {
      loadBlogPosts();
    }
  }
);

const getBlogTypeString = (template) => {
  if (template === "project") return t("blog.Project");
  if (template === "how-to") return t("blog.How_To");
  if (template === "news") return t("blog.News");
  if (template === "feature") return t("blog.Feature");
};

// Filter posts based on selected tag
const filteredPosts = computed(() => {
  if (selectedFilter.value === "all") {
    return blogPosts.value;
  }
  return blogPosts.value.filter((post) => post.template === selectedFilter.value);
});
</script>

<style scoped lang="scss">
@import "../../scss/styles.scss";

.app-blog-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
}

.blog-posts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
  margin-bottom: 2rem;
}

.blog-post-card {
  background: white;
  border-radius: 1rem;
  overflow: hidden;
  transition: transform 0.2s;
  border: 1px solid var(--knittable-lav-light);
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-4px);
  }
}

.blog-post-link {
  text-decoration: none;
  color: inherit;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.blog-post-image {
  width: 100%;
  height: 200px;
  overflow: hidden;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.blog-post-content {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  flex-grow: 1;

  h2 {
    @include heading6;
    color: var(--knittable-lav-darker);
    margin-bottom: 0.5rem;
    line-height: 1.2;
    min-height: 2.4em;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-word;
    hyphens: none;
  }
}

.blog-post-excerpt {
  @include paragraphMediumRegular;
  color: var(--knittable-mocha);
  margin-bottom: 1rem;
  flex-grow: 1;

  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  max-height: 4.5em;
}

.blog-post-meta {
  @include paragraphSmallRegular;
  color: var(--knittable-grey-darker);
  margin-top: auto;
}

.blog-post-author-date-separator {
  margin: 0 0.5rem;
}

.blog-type-tag {
  @include paragraphSmallBold;
  position: absolute;
  top: 1rem;
  right: 1rem;
  padding: 0.5rem 1rem;
  border-radius: 1rem;
  color: white;
}

.type-project {
  background-color: var(--knittable-lav);
}

.type-how-to {
  background-color: var(--knittable-verdigris);
}

.type-news {
  background-color: var(--knittable-jade);
}

.type-feature {
  background-color: var(--knittable-begonia);
}

.blog-filter-bar {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin: 2rem 0;
  flex-wrap: wrap;
}

.filter-tag {
  padding: 0.5rem 1rem;
  border-radius: 1rem;
  border: none;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  text-transform: capitalize;
  transition: all 0.2s ease;
  background-color: var(--knittable-grey-light);
  color: var(--knittable-grey-darker);

  &:hover {
    transform: translateY(-2px);
  }

  &.active {
    color: white;

    &.type-project {
      background-color: var(--knittable-lav);
    }

    &.type-how-to {
      background-color: var(--knittable-verdigris);
    }

    &.type-news {
      background-color: var(--knittable-jade);
    }

    &.type-feature {
      background-color: var(--knittable-begonia);
    }

    &.type-all {
      background-color: var(--knittable-grey-darker);
    }
  }

  &:not(.active) {
    &:hover {
      background-color: var(--knittable-grey);
    }
  }
}

// Make filter bar responsive
.app-sm-and-down {
  .blog-filter-bar {
    padding: 0 1rem;
    gap: 0.5rem;
  }
}
</style>
